import clsx from "clsx"
import { animate, motion, useMotionValue, useTransform } from "framer-motion"
import { useEffect, useRef } from "react"

const defaultFormatOptions: Intl.NumberFormatOptions = {
  style: "currency",
  currencyDisplay: "narrowSymbol",
  maximumFractionDigits: 2,
}

type CurrencyPrecision = "default" | "tradingPrice" | "portfolioPrice"

function getPrecisionFormatOptions(
  value: number,
  precision: CurrencyPrecision
): Intl.NumberFormatOptions {
  if (precision === "default") return {}

  const absValue = Math.abs(value)

  if (precision === "portfolioPrice") {
    if (absValue > 0 && absValue < 0.01) {
      return {
        minimumFractionDigits: 2,
        maximumFractionDigits: 6,
      }
    }

    return {}
  }

  if (absValue === 0) {
    return {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }
  }
  if (absValue < 0.01) {
    return {
      minimumFractionDigits: 6,
      maximumFractionDigits: 6,
    }
  }
  if (absValue < 1) {
    return {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    }
  }
  if (absValue < 1000) {
    return {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  }
  return {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }
}

function splitFormattedParts(formatter: Intl.NumberFormat, v: number) {
  const parts = formatter.formatToParts(v)
  const decimalIdx = parts.findIndex((part) => part.type === "decimal")
  if (decimalIdx === -1) {
    return {
      main: parts.map((part) => part.value).join(""),
      cents: "",
    }
  }
  return {
    main: parts
      .slice(0, decimalIdx)
      .map((part) => part.value)
      .join(""),
    cents: parts
      .slice(decimalIdx)
      .map((part) => part.value)
      .join(""),
  }
}

export function FormattedCurrency({
  value,
  locale,
  formatOptions,
  className,
  mainPartClassName,
  centsClassName,
  hideBalances,
  loading,
  animated = false,
  showSigned = false,
  precision = "default",
}: {
  value: number
  formatOptions: Intl.NumberFormatOptions
  locale?: string
  className?: string
  mainPartClassName?: string
  centsClassName?: string
  hideBalances?: boolean
  loading?: boolean
  animated?: boolean
  showSigned?: boolean
  precision?: CurrencyPrecision
}) {
  const normalizedValue = showSigned ? Math.abs(value) : value
  const signPrefix = showSigned ? (value > 0 ? "+" : value < 0 ? "-" : "") : ""

  const formatter = new Intl.NumberFormat(locale, {
    ...defaultFormatOptions,
    ...formatOptions,
    ...getPrecisionFormatOptions(normalizedValue, precision),
  })
  const formatterRef = useRef(formatter)
  formatterRef.current = formatter

  const animatedValue = useMotionValue(normalizedValue)

  useEffect(() => {
    if (!animated) {
      animatedValue.set(normalizedValue)
      return
    }
    const controls = animate(animatedValue, normalizedValue, {
      duration: 0.5,
      ease: "easeOut",
    })
    return () => controls.stop()
  }, [normalizedValue, animated, animatedValue])

  const mainPart = useTransform(
    () => splitFormattedParts(formatterRef.current, animatedValue.get()).main
  )
  const centsPart = useTransform(
    () => splitFormattedParts(formatterRef.current, animatedValue.get()).cents
  )

  const staticParts = animated
    ? null
    : splitFormattedParts(formatter, normalizedValue)

  const mainClassName = clsx(
    mainPartClassName,
    "transition-colors duration-200",
    loading && "animate-pulse text-gray-400"
  )
  const centsClass = clsx(
    centsClassName,
    "transition-colors duration-200",
    loading && "animate-pulse text-gray-400"
  )

  return (
    <div className={clsx(className)}>
      {hideBalances ? (
        <span
          className={clsx(
            mainPartClassName,
            "-tracking-[0.07em]! text-gray-700! dark:text-gray-300!"
          )}
        >
          ••••••
        </span>
      ) : animated ? (
        <>
          {signPrefix && <span className={mainClassName}>{signPrefix}</span>}
          <motion.span className={mainClassName}>{mainPart}</motion.span>
          <motion.span className={centsClass}>{centsPart}</motion.span>
        </>
      ) : (
        <>
          {signPrefix && <span className={mainClassName}>{signPrefix}</span>}
          <span className={mainClassName}>{staticParts?.main}</span>
          <span className={centsClass}>{staticParts?.cents}</span>
        </>
      )}
    </div>
  )
}
